import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Polyline, Popup, useMap } from 'react-leaflet';
import L, { LatLngBounds } from 'leaflet';

/**
 * Reusable canvas-rendered polyline layer (PRD-04 FR2), generalised from
 * `OsmRouteLayerRenderer`: one shared `L.canvas` instead of N SVG paths,
 * viewport-culled, capped, highlighted-first ordering, click → callback.
 *
 * Presentational only — the caller (e.g. `NetworkRoutesLayer`) fetches + decodes the
 * geometry per viewport and passes it in. Keeps bulk geometry out of React state.
 */
export interface CanvasLine {
  id: string;
  coordinates: Array<[number, number]>; // [lat, lng]
  color?: string | null;
  weight?: number;
  opacity?: number;
}

interface Props {
  lines: CanvasLine[];
  highlightedIds?: Set<string>;
  cap?: number;
  onLineClick?: (id: string) => void;
  /**
   * When false (network display), the `lines` are already viewport-scoped by the
   * server, so we skip client-side bounds culling — that avoids the flicker where
   * features get hidden on zoom before the (debounced) refetch lands. Leaflet's
   * canvas repositions the existing polylines on pan/zoom without a React re-render.
   */
  viewportCull?: boolean;
  /** Optional popup content per line (bound to the polyline, opened on click by Leaflet). */
  renderPopup?: (id: string) => React.ReactNode;
  /**
   * Shared canvas renderer. Pass one renderer to every co-located vector layer so they
   * draw onto a *single* `<canvas>` — Leaflet 1.x routes a DOM click only to the topmost
   * canvas pane, so stacking multiple `L.canvas()` panes makes upper layers swallow clicks
   * meant for lines beneath them. Falls back to a private renderer when omitted.
   */
  renderer?: L.Canvas;
  /** When false the lines are decorative (no hit-testing) so clicks fall through to the layer beneath. */
  interactive?: boolean;
}

const DEFAULT_CAP = 600;
const DEFAULT_COLOR = '#1976d2';

const CanvasLineLayer: React.FC<Props> = ({ lines, highlightedIds, cap = DEFAULT_CAP, onLineClick, viewportCull = true, renderPopup, renderer, interactive = true }) => {
  const map = useMap();
  const internalRenderer = useRef(L.canvas({ padding: 0.5 }));
  const activeRenderer = renderer ?? internalRenderer.current;
  const [bounds, setBounds] = useState<LatLngBounds>(() => map.getBounds());

  useEffect(() => {
    if (!viewportCull) return undefined; // data is pre-scoped; don't churn on move
    const onChange = () => setBounds(map.getBounds());
    map.on('moveend', onChange);
    map.on('zoomend', onChange);
    return () => {
      map.off('moveend', onChange);
      map.off('zoomend', onChange);
    };
  }, [map, viewportCull]);

  const onClickRef = useRef(onLineClick);
  useEffect(() => {
    onClickRef.current = onLineClick;
  }, [onLineClick]);

  const elements = useMemo(() => {
    const padded = viewportCull ? bounds.pad(0.2) : null;
    const hi = highlightedIds ?? new Set<string>();
    // highlighted first so the cap never drops a selected route
    const ordered = [...lines].sort((a, b) => Number(hi.has(b.id)) - Number(hi.has(a.id)));
    const out: React.ReactElement[] = [];
    let rendered = 0;

    for (const line of ordered) {
      const positions = line.coordinates;
      if (!positions || positions.length < 2) continue;
      const selected = hi.has(line.id);
      if (!selected) {
        if (padded) {
          try {
            const b = L.latLngBounds(positions.map((p) => L.latLng(p[0], p[1])));
            if (!padded.intersects(b)) continue;
          } catch {
            continue;
          }
        }
        if (rendered >= cap) continue;
      }
      rendered += 1;
      // Thin weights so a whole network reads cleanly (FR11); selected stands out.
      const weight = line.weight ?? 2;
      out.push(
        <Polyline
          key={line.id}
          positions={positions}
          pathOptions={{
            color: line.color || DEFAULT_COLOR,
            weight: selected ? weight + 2 : weight,
            opacity: selected ? 1 : line.opacity ?? 0.75,
            renderer: activeRenderer,
            interactive,
          }}
          eventHandlers={interactive ? { click: () => onClickRef.current?.(line.id) } : undefined}
        >
          {renderPopup ? <Popup>{renderPopup(line.id)}</Popup> : null}
        </Polyline>,
      );
    }
    return out;
  }, [lines, highlightedIds, bounds, cap, viewportCull, renderPopup, activeRenderer, interactive]);

  return <>{elements}</>;
};

export default CanvasLineLayer;
